import {
  ArrowLeftIcon,
  Pencil2Icon,
  SewingPinFilledIcon,
  StarFilledIcon,
  StarIcon,
} from '@radix-ui/react-icons';
import { type PropsWithChildren } from 'react';
import {
  Link,
  Outlet,
  useLoaderData,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';
import { Ok, Result } from 'ts-results-es';

import { getHints } from '~/components/client-hints';
import { GymFavoriteForm } from '~/components/favorites/gym-favorite-form';
import { TabLink } from '~/components/nav/tab-link';
import { SharePageButton } from '~/components/share-page-button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~/components/ui/accordion';
import { Button } from '~/components/ui/button';
import { getGymDropInPassForUserId } from '~/domain/drop-in-passes/handlers/get-drop-in-pass-for-user.server';
import { getDropInPassesForGym } from '~/domain/drop-in-passes/handlers/get-drop-in-passes-for-gym.server';
import { getGymPhotosForGym } from '~/domain/gym-photos/queries/get-gym-photos-for-gym.server';
import { getAthleteGymDetailsById } from '~/domain/gyms/queries/get-athlete-gym-details-by-id.server';
import { getPlansForGym } from '~/domain/plans/handlers/get-plans-for-gym.server';
import { getGymMembershipForUserId } from '~/domain/plans-and-memberships/handlers/get-membership-for-user.server';
import { type UserDropInPass } from '~/domain/user-drop-in-passes/models/user-drop-in-pass';
import { getSessionIfAuthenticated } from '~/utils/auth.server';
import { hasPermissionForGymWithId } from '~/utils/data-auth/gym-auth.server';
import { handleDomainError } from '~/utils/errors.server';
import { getGymIdAndSlugFromParams } from '~/utils/gyms.server';

import { renderAverageRating } from '../components/ratings';

import { type Route } from './+types/_layout';
import { GymPhotos } from './components/gym-photos';
import { PurchaseDropInButton } from './resources+/purchase-drop-in';
import { PurchaseMembershipButton } from './resources+/purchase-membership';

export const handle = {
  title: 'Find Gyms & Classes',
};

export async function loader({ request, params }: Route.LoaderArgs) {
  const session = await getSessionIfAuthenticated(request);

  const { gymId } = await getGymIdAndSlugFromParams(params);
  const canEditGym = session
    ? await hasPermissionForGymWithId(request, gymId, ['GymDetails.Update'])
    : false;
  const { timeZone } = getHints(request);

  const queryResults = await Promise.all([
    getAthleteGymDetailsById(gymId, session?.userId),
    getGymPhotosForGym(gymId),
    getDropInPassesForGym(gymId),
    getPlansForGym(gymId),
    session ? getGymDropInPassForUserId(session.userId, gymId) : Ok(null),
    session ? getGymMembershipForUserId(session.userId, gymId) : Ok(null),
  ]);
  const result = Result.all(...queryResults);

  return result
    .map(([gym, photos, gymWithDropInPasses, gymPlans, userDropInPass, userMembership]) => ({
      gym,
      photos,
      gymDropInPasses: gymWithDropInPasses.dropInPasses,
      gymPlans,
      userDropInPass,
      userMembership,
      canEditGym,
      timeZone,
    }))
    .mapErr(handleDomainError)
    .unwrap();
}

interface GymPurchaseItemProps {
  triggerTitle: string;
  itemValue: string;
  hasPurchased: boolean;
}

function GymPurchaseItem({
  triggerTitle,
  itemValue,
  hasPurchased,
  children,
}: PropsWithChildren<GymPurchaseItemProps>) {
  return (
    <>
      {!hasPurchased ? (
        <AccordionItem className="border-none" value={itemValue}>
          <AccordionTrigger className="text-body1 leading-none hover:no-underline">
            {triggerTitle}
          </AccordionTrigger>
          <AccordionContent contentDivClassName="pb-0">{children}</AccordionContent>
        </AccordionItem>
      ) : (
        children
      )}
    </>
  );
}

export default function Details() {
  const {
    gym,
    photos,
    gymDropInPasses,
    gymPlans,
    userDropInPass,
    userMembership,
    canEditGym,
    timeZone,
  } = useLoaderData<typeof loader>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const stateAndZip = gym.stateAbbreviation && gym.zip ? `${gym.stateAbbreviation} ${gym.zip}` : '';

  const fullAddress = [gym.addressLine1, gym.addressLine2, gym.city, stateAndZip]
    .filter((x) => x)
    .join(', ');

  const gymLocation = gym.neighborhood ? gym.neighborhood : fullAddress;

  const handleEdit = () => {
    navigate(`/admin/gyms/${gym.id}/details`);
  };

  const returnQuery = searchParams.get('query');
  const returnRoute = `${location.pathname}${location.search}`;

  const hasMembership = userMembership !== null;
  const hasDropInPass = userDropInPass !== null;
  const hasStripe = gym.stripeConnectAccountId !== null && gym.stripeSetupComplete === true;
  const showMembershipSection = hasStripe && (gymPlans.length > 0 || hasMembership);
  const showDropInPassesSection = hasStripe && (gymDropInPasses.length > 0 || hasDropInPass);

  return (
    <div className="mx-4 my-6 pb-12">
      {returnQuery ? (
        <div className="mx-auto mb-6 max-w-md desktop:max-w-screen-lg">
          <Link to={`/gym?${returnQuery}`} className="text-caption">
            <ArrowLeftIcon className="mr-1 inline-block" /> Back to Results
          </Link>
        </div>
      ) : null}
      <div className="mx-auto flex max-w-md flex-wrap gap-8 desktop:max-w-screen-lg">
        {/* left col */}
        <div className="shrink basis-[450px]">
          {canEditGym ? (
            <div className="col-start-2 mb-2">
              <Button variant="outline" title="Edit gym" className="w-28" onClick={handleEdit}>
                Edit <Pencil2Icon className="ml-2 h-4 w-4" />
              </Button>
            </div>
          ) : null}
          {/* photos */}
          <GymPhotos urls={photos.map((p) => p.url)} />
          {/* details */}
          <div className="mt-2 grid grid-cols-[auto_1fr] gap-1 gap-x-2">
            <GymFavoriteForm
              gymSlug={gym.urlSlug ?? gym.id}
              isFavorite={gym.isFavorite}
              returnRoute={returnRoute}
              size="icon-sm"
            />
            <h1 className="col-start-2 break-all font-sans text-h6 font-bold text-primary">
              {gym.name}
            </h1>

            {gym.tagline ? (
              <h2 className="col-start-2 font-sans text-body1 font-light italic text-primary">
                {gym.tagline}
              </h2>
            ) : null}

            {gymLocation ? (
              <>
                <SewingPinFilledIcon className="col-start-1 size-6 self-center" />
                <p className="font-sans text-body1 font-light text-primary">{gymLocation}</p>
              </>
            ) : null}

            {gym.numberOfRatings === 0 ? (
              <>
                <StarIcon className="col-start-1 size-6 self-center text-primary-light" />
                <p className="text-body1">Not rated</p>
              </>
            ) : (
              <>
                <StarFilledIcon className="col-start-1 self-center text-accent" />
                <p className="text-body1">
                  {renderAverageRating(gym.averageRating)} ({gym.numberOfRatings})
                </p>
              </>
            )}
          </div>
          {/* contact */}
          <div className="mt-4 flex flex-wrap gap-2 tablet:gap-4">
            {gym.phoneNumber ? (
              <Button asChild variant="outline" className="w-auto flex-1">
                <a href={`tel:${gym.phoneNumber}`}>Call</a>
              </Button>
            ) : null}

            {gym.email ? (
              <Button asChild variant="outline" className="w-auto flex-1">
                <a href={`mailto:${gym.email}`}>Email</a>
              </Button>
            ) : null}

            {gym.url ? (
              <Button asChild variant="outline" className="w-auto flex-1">
                <a href={gym.url} target="_blank" rel="noreferrer">
                  Website
                </a>
              </Button>
            ) : null}

            <SharePageButton
              className="w-auto flex-1"
              variant="outline"
              getUrl={() =>
                `${window.location.protocol}//${window.location.host}${window.location.pathname}`
              }
            />
          </div>
          {showMembershipSection || showDropInPassesSection ? (
            <div className="mt-4 rounded-lg bg-gray-200 p-4">
              <h6>
                {hasDropInPass || hasMembership ? 'Your Memberships & Passes' : 'Get Instant Access'}
              </h6>
              <Accordion
                type="single"
                defaultValue={
                  // If there's only one section, expand it by default
                  showMembershipSection && !showDropInPassesSection
                    ? 'memberships'
                    : showDropInPassesSection && !showMembershipSection
                      ? 'drop-ins'
                      : undefined
                }
                collapsible
                className="mt-4"
              >
                {showMembershipSection ? (
                  <GymPurchaseItem
                    triggerTitle="Memberships"
                    itemValue="memberships"
                    hasPurchased={hasMembership}
                  >
                    <PurchaseMembershipButton
                      gymSlugOrId={gym.urlSlug ?? gym.id}
                      gymPlans={gymPlans}
                      userMembership={userMembership}
                    />
                  </GymPurchaseItem>
                ) : null}
                {showMembershipSection && showDropInPassesSection ? (
                  <hr className="my-4 border-gray-900" />
                ) : null}
                {showDropInPassesSection ? (
                  <GymPurchaseItem
                    triggerTitle="Drop-In Passes"
                    itemValue="drop-ins"
                    hasPurchased={hasDropInPass}
                  >
                    <PurchaseDropInButton
                      gymSlugOrId={gym.urlSlug ?? gym.id}
                      gymDropInPasses={gymDropInPasses}
                      timeZone={timeZone}
                      userDropInPass={userDropInPass as UserDropInPass | null}
                    />
                  </GymPurchaseItem>
                ) : null}
              </Accordion>
            </div>
          ) : null}
        </div>

        {/* right col */}
        <div className="max-w-[450px] shrink basis-[450px]">
          <div className="h-10 w-full">
            <TabLink
              className="inline-block h-[49px] w-1/4"
              to={`/gym/${gym.urlSlug ?? gym.id}/details`}
            >
              Details
            </TabLink>
            <TabLink
              className="inline-block h-[49px] w-1/4"
              to={`/gym/${gym.urlSlug ?? gym.id}/calendar`}
            >
              Calendar
            </TabLink>
            <TabLink
              className="inline-block h-[49px] w-1/4"
              to={`/gym/${gym.urlSlug ?? gym.id}/classes`}
            >
              Classes
            </TabLink>
            <TabLink
              className="inline-block h-[49px] w-1/4"
              to={`/gym/${gym.urlSlug ?? gym.id}/events`}
            >
              Events
            </TabLink>
          </div>
          <div className="py-8">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
